import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import {useNavigate } from "react-router-dom";

import { LABELS } from "~/resources/Labels";
import { MESSAGES } from "~/resources/Message";

import { SIMPLInput } from "../../components/commons/input/SIMPLInput";
import { SIMPLSelectDropdown } from "../../components/commons/input/SIMPLSelectDropdown";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { SliderField } from "../../components/commons/slider/SIMPLSlider";
import { SIMPLMenuHeader } from "../../components/commons/header/SIMPLMenuHeader";
import { SIMPLBreadcrumb } from "../../components/commons/breadcrumb/SimplBreadCrumb";
import { LoadingWrapper } from "../../components/commons/spiner/SimpleLoaderWrapper";

import { UseVmTemplateForm } from "./UseVmTemplateForm";

import {
  extractCloudProviderList,
  extractComponentsDetails,
  extractEnvDetails,
  getProviders,
} from "~/Utils/Utils";

import { useGetVmTemplatesQuery } from "~/services/vm/VmTemplate";
import { useGetCloudProvisionerFullQuery } from "~/services/cloudProvisioner";
import { FormCloudEnvironment } from "~/models/Commons ";

// -------------------- Types --------------------
interface Prop {
  editedItem: FormCloudEnvironment | null;
  onSuccess?: (title: string, message: string, response: any) => void;
  onError?: (title: string, message: string) => void;
}

export const AddVmTemplate = ({ editedItem, onSuccess, onError }: Prop) => {
  const { refetch } = useGetVmTemplatesQuery({
    page: 0,
    size: 999,
    active: false,
  });

  const {
    handleSubmit,
    control,
    errors,
    cloudEnvData,
    cloudProviderName,
    ConfigurationComponentsData,
    PolicyComponentsData,
    ProvissioningComponentsData,
    providerData,
    provisionerData,
    submitForm,
    setValue,
    watch,
    resetField,
    isLoading,
    isError,
    trigger,
  } = UseVmTemplateForm(refetch, undefined!);

  const navigate = useNavigate();

  const [cloudProviderList, setCloudProviderList] = useState<any[]>([]);
  const [cpu, setCpu] = useState<{ min: number; max: number } | null>(null);
  const [ram, setRam] = useState<{ min: number; max: number } | null>(null);
  const [storage, setStorage] = useState<{ min: number; max: number } | null>(
    null,
  );
  const [cloudProvisionerTemplateId, setCloudProvisionerTemplateId] = useState<
    number | undefined
  >(undefined);
  const [osOptions, setOsOptions] = useState<string[]>([]);
  const [selectClouldProvisioner, setSelectClouldProvisioner] = useState<any[]>(
    [],
  );
  const [clouldProvisionerList, setClouldProvisionerList] = useState<any[]>([]);
  const [selectedCloudEnvironments, setSelectedCloudEnvironments] = useState<
    any[]
  >([]);
  const [selectCloudEnv, setSelectCloudEnv] = useState<any[]>([]);
  const [selectProvider, setSelectProvider] = useState<any[]>([]);
  const [selectVmConfiguration, setSelectVmConfiguration] = useState<any[]>([]);
  const [selectPostProvisioning, setSelectPostProvisioning] = useState<any[]>(
    [],
  );
  const [selectPolicy, setSelectPolicy] = useState<any[]>([]);
  const [showOvgInput, setShowOvgInput] = useState<boolean>(false);

  const { data } = useGetCloudProvisionerFullQuery();

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit(submitForm)(event);
  };

  useEffect(() => {
    setCloudProviderList(getProviders(providerData));
  }, [providerData]);

  const getCloudProviderOptions = (
    provider: { cloudProviderName: string; id: number }[],
  ) =>
    provider?.map((cloudEnv) => ({
      label: cloudEnv.cloudProviderName,
      value: cloudEnv.id,
    }));

  const handleCloudProviderChange = (
    value: number,
    onChange: (val: number) => void,
  ) => {
    onChange(value);

    const selected = cloudProviderName.find((p) => p.id === value);
    const isOvh = selected?.cloudProviderName === LABELS.Ovh;

    setShowOvgInput(isOvh);

    resetField("os");

    if (isOvh) {
      resetField("cpuCore");
      resetField("ram");
      resetField("storage");
    }
  };

  const getProvisionerValues = (
    provisionerPayload: { content?: any[] } | undefined,
    selectedId?: number,
  ) => {
    const list = provisionerPayload?.content ?? [];

    if (!selectedId) {
      return {
        minCpuCore: null,
        maxCpuCore: null,
        minRam: null,
        maxRam: null,
        minStorage: null,
        maxStorage: null,
        os: [],
      };
    }

    const item = list.find((i) => i.id === selectedId);

    if (!item) {
      return {
        minCpuCore: null,
        maxCpuCore: null,
        minRam: null,
        maxRam: null,
        minStorage: null,
        maxStorage: null,
        os: [],
      };
    }

    return {
      minCpuCore: item.minCpuCore ?? 0,
      maxCpuCore: item.maxCpuCore ?? 0,
      minRam: item.minRam ?? 0,
      maxRam: item.maxRam ?? 0,
      minStorage: item.minStorage ?? 0,
      maxStorage: item.maxStorage ?? 0,
      os: item.os ?? [],
    };
  };

  const initVmTemplate = () => {
    const provisionerPayload: any = provisionerData?.payload;
    const envPayload = cloudEnvData?.payload as any | undefined;
    const configPayload = ConfigurationComponentsData?.payload as
      | any
      | undefined;
    const postProvisionPayload = ProvissioningComponentsData?.payload as
      | any
      | undefined;
    const policyPayload = PolicyComponentsData?.payload as any | undefined;

    if (provisionerPayload?.content) {
      setClouldProvisionerList(provisionerPayload.content);
      setSelectClouldProvisioner(
        provisionerPayload.content.map(({ id, fileName }: any) => ({
          id,
          fileName,
        })),
      );
    }

    if (envPayload) setSelectCloudEnv(extractEnvDetails(envPayload.content));
    setSelectedCloudEnvironments(envPayload?.content);
    setSelectProvider(extractCloudProviderList(envPayload?.content));

    if (configPayload)
      setSelectVmConfiguration(extractComponentsDetails(configPayload.content));
    if (postProvisionPayload)
      setSelectPostProvisioning(
        extractComponentsDetails(postProvisionPayload.content),
      );
    if (policyPayload)
      setSelectPolicy(extractComponentsDetails(policyPayload.content));
  };

  useEffect(() => {
    initVmTemplate();
  }, [
    data,
    provisionerData,
    cloudEnvData,
    ConfigurationComponentsData,
    PolicyComponentsData,
    ProvissioningComponentsData,
    editedItem,
  ]);

  const provider = watch("providerType");
  const environment = watch("cloudEnvironmentId");
  const selectedId = watch("cloudProvisionerTemplateId");

  useEffect(() => {
    if (!provider) return;

    const isOvh = provider.label.trim().toLowerCase() === "ovh";
    setShowOvgInput(isOvh);

    if (watch("cloudEnvironmentId") !== null) {
      setValue("cloudEnvironmentId", null);
    }

    if (watch("cloudProvisionerTemplateId") !== null) {
      setValue("cloudProvisionerTemplateId", null);
    }
  }, [provider]);

  useEffect(() => {
    if (!environment) return;

    if (watch("cloudProvisionerTemplateId") !== null) {
      setValue("cloudProvisionerTemplateId", null);
    }
  }, [environment]);

  useEffect(() => {
    const provisionerPayload: any = provisionerData?.payload;
    if (!provisionerPayload) return;

    const {
      minCpuCore,
      maxCpuCore,
      minRam,
      maxRam,
      minStorage,
      maxStorage,
      os,
    } = getProvisionerValues(provisionerPayload, selectedId?.value);

    setCpu((prev) =>
      prev?.min === minCpuCore && prev?.max === maxCpuCore
        ? prev
        : { min: minCpuCore, max: maxCpuCore },
    );

    setRam((prev) =>
      prev?.min === minRam && prev?.max === maxRam
        ? prev
        : { min: minRam, max: maxRam },
    );

    setStorage((prev) =>
      prev?.min === minStorage && prev?.max === maxStorage
        ? prev
        : { min: minStorage, max: maxStorage },
    );

    setOsOptions((prev) =>
      JSON.stringify(prev) === JSON.stringify(os) ? prev : os,
    );
  }, [selectedId, provisionerData]);

  const filteredEnvironments = provider
    ? (selectedCloudEnvironments?.filter(
        (env) => env.cloudProvider?.cloudProviderName === provider.label,
      ) ?? [])
    : selectCloudEnv;

  const filteredCloudProvisionerTemplate = provider
    ? (clouldProvisionerList?.filter((env) =>
        provider.label.trim().toLowerCase() === "ovh" ? env.isOvh : !env.isOvh,
      ) ?? [])
    : selectClouldProvisioner;

  const watchProvisioner = watch("cloudProvisionerTemplateId");

  useEffect(() => {
    const newValue = watchProvisioner?.value;

    if (cloudProvisionerTemplateId !== newValue) {
      setCloudProvisionerTemplateId(newValue);

      if (watch("os") !== null) {
        setValue("os", null);
      }
    }
  }, [watchProvisioner, cloudProvisionerTemplateId]);

  const handleCancel = () => navigate("/vm-template");

  const validateAtLeastOne = () => {
    const values = watch(["vmconfiguration", "vmPostProviosiing", "vmPolicy"]);
    return values.some(Boolean) || "Select at least one option";
  };

  return (
    <SIMPLMenuHeader>
      <LoadingWrapper
        loadingTitle="Loading VmTemplate..."
        isLoading={isLoading}
        isError={isError}
        testId="vmTemplate-loading"
      >
        <div className="flex justify-center bg-white px-6 py-8">
          <div className="w-full max-w-[1104px] rounded-xl border border-[#D6D6D6] bg-white">
            <div className="px-8 pt-6">
              <SIMPLBreadcrumb />
              <h1 className="mt-2 font-['Arial'] text-[28px] font-bold leading-[44px] text-left text-Foundation/Heading normal-case">
                {LABELS.VmTemplate}
              </h1>
            </div>

            <form onSubmit={handleFormSubmit}>
              <div className="px-8 pb-8 pt-6 space-y-6">
                {/* NAME & DESCRIPTION */}
                <div className="space-y-6 pt-2">
                  <SIMPLInput
                    name="name"
                    control={control}
                    rules={{
                      required: { value: true, message: LABELS.NameRequired },
                    }}
                    label={LABELS.Name}
                    placeholder={LABELS.Name}
                    required
                    id="name"
                    fontWeight="bold"
                    background="white"
                    errorLabel={errors.name?.message}
                    testId="vmTemplate-name"
                  />
                </div>

                <div className="space-y-6 pt-2">
                  <SIMPLInput
                    name="description"
                    control={control}
                    label={LABELS.Description}
                    id="description"
                    fontWeight="bold"
                    background="white"
                    errorLabel={errors.description?.message}
                    placeholder={LABELS.Description}
                    testId="vmTemplate-description"
                  />
                </div>

                {/* CLOUD PROVIDER SELECT */}
                <div className="space-y-6 pt-2">
                  <Controller
                    name="providerType"
                    control={control}
                    rules={{ required: MESSAGES.MessageCloudProviderRequired }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="providerType"
                        label={LABELS.CloudProviderWithSpace}
                        options={getCloudProviderOptions(cloudProviderList)}
                        isRequired
                        value={field.value}
                        onChange={(val: any) =>
                          handleCloudProviderChange(val, field.onChange)
                        }
                        hideErrorLabel={false}
                        errorLabel={errors.providerType?.message as string}
                        placeholder={LABELS.CloudProviderWithSpace}
                        background="white"
                        testId="vmTemplate-providerType"
                      />
                    )}
                  />
                </div>

                {/* CLOUD ENVIRONMENT SELECT */}
                <div className="space-y-6 pt-2">
                  <Controller
                    name="cloudEnvironmentId"
                    control={control}
                    rules={{
                      required: MESSAGES.MessageCloudEnvironmentRequired,
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="cloudEnvironmentId"
                        label={LABELS.CloudEnviroment}
                        options={filteredEnvironments?.map((cloudEnv) => ({
                          label: cloudEnv.environmentName,
                          value: cloudEnv.id,
                        }))}
                        isRequired
                        isDisabled={!provider}
                        value={field.value}
                        onChange={field.onChange}
                        hideErrorLabel={false}
                        errorLabel={
                          errors.cloudEnvironmentId?.message as string
                        }
                        placeholder={LABELS.CloudEnviroment}
                        background="white"
                        testId="vmTemplate-cloudenvironmentId"
                      />
                    )}
                  />
                </div>

                {/* CLOUD PROVISIONER TEMPLATE SELECT */}
                <div className="space-y-6 pt-2">
                  <Controller
                    name="cloudProvisionerTemplateId"
                    control={control}
                    rules={{
                      required: MESSAGES.MessageProvisioningTemplateRequired,
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="cloudProvisionerTemplateId"
                        label={LABELS.ProvisioningTemplate}
                        options={filteredCloudProvisionerTemplate.map(
                          (template) => ({
                            label: template.fileName,
                            value: template.id,
                          }),
                        )}
                        isRequired
                        isDisabled={!provider}
                        value={field.value}
                        onChange={field.onChange}
                        hideErrorLabel={false}
                        errorLabel={
                          errors.cloudProvisionerTemplateId?.message as string
                        }
                        placeholder={LABELS.ProvisioningTemplate}
                        background="white"
                        testId="vmTemplate-cloudProvisionerTemplateId"
                      />
                    )}
                  />
                </div>

                {/* VM CONFIG / POST PROVISIONING / POLICY */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Controller
                    name="vmconfiguration"
                    control={control}
                    rules={{ validate: validateAtLeastOne }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="vmconfiguration"
                        label={LABELS.VmConfiguration}
                        options={selectVmConfiguration.map((c) => ({
                          label: c.name,
                          value: c.id,
                        }))}
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                          trigger([
                            "vmconfiguration",
                            "vmPostProviosiing",
                            "vmPolicy",
                          ]);
                        }}
                        errorLabel={errors.vmconfiguration?.message as string}
                        placeholder={LABELS.VmConfiguration}
                        background="white"
                        testId="vmTemplate-vm-configuration"
                      />
                    )}
                  />

                  <Controller
                    name="vmPostProviosiing"
                    control={control}
                    rules={{ validate: validateAtLeastOne }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="vmPostProviosiing"
                        label={LABELS.VmProvisioning}
                        options={selectPostProvisioning.map((p) => ({
                          label: p.name,
                          value: p.id,
                        }))}
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                          trigger([
                            "vmconfiguration",
                            "vmPostProviosiing",
                            "vmPolicy",
                          ]);
                        }}
                        errorLabel={errors.vmPostProviosiing?.message as string}
                        placeholder={LABELS.VmProvisioning}
                        background="white"
                        testId="vmTemplate-vm-postProvisioning"
                      />
                    )}
                  />

                  <Controller
                    name="vmPolicy"
                    control={control}
                    rules={{ validate: validateAtLeastOne }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="vmPolicy"
                        label={LABELS.VmPolicy}
                        options={selectPolicy.map((p) => ({
                          label: p.name,
                          value: p.id,
                        }))}
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                          trigger([
                            "vmconfiguration",
                            "vmPostProviosiing",
                            "vmPolicy",
                          ]);
                        }}
                        errorLabel={errors.vmPolicy?.message as string}
                        placeholder={LABELS.VmPolicy}
                        background="white"
                        testId="vmTemplate-vm-policy"
                      />
                    )}
                  />

                  <Controller
                    name="os"
                    control={control}
                    rules={{ required: "Os is required" }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="os"
                        label={LABELS.Os}
                        options={osOptions?.map((os) => ({
                          label: os,
                          value: os,
                        }))}
                        value={field.value}
                        onChange={field.onChange}
                        hideErrorLabel={false}
                        isRequired
                        errorLabel={errors.os?.message as string}
                        placeholder={LABELS.SelectOs}
                        background="white"
                        testId="vmTemplate-os"
                      />
                    )}
                  />
                </div>

                {/* SLIDERS */}
                {!showOvgInput && (
                  <>
                    <div className="space-y-6 pt-2">
                      <Controller
                        name="cpuCore"
                        control={control}
                        rules={{ required: "CPU Core is required" }}
                        render={({ field, fieldState }) => (
                          <SliderField
                            id="cpuCores"
                            label={LABELS.CpuCore}
                            min={cpu?.min}
                            max={cpu?.max}
                            value={field.value}
                            valueLabel={LABELS.CpuCoreValue}
                            onChange={field.onChange}
                            errorLabel={fieldState.error?.message}
                            required
                            testId="vmTemplate-cpuCore"
                          />
                        )}
                      />
                    </div>

                    <div className="space-y-6 pt-2">
                      <Controller
                        name="ram"
                        control={control}
                        rules={{ required: "RAM is required" }}
                        render={({ field, fieldState }) => (
                          <SliderField
                            id="ram"
                            label={LABELS.Ram}
                            min={ram?.min}
                            max={ram?.max}
                            value={field.value}
                            valueLabel={LABELS.RamValue}
                            onChange={field.onChange}
                            errorLabel={fieldState.error?.message}
                            required
                            testId="vmTemplate-ram"
                          />
                        )}
                      />
                    </div>

                    <div className="space-y-6 pt-2">
                      <Controller
                        name="storage"
                        control={control}
                        rules={{ required: "Storage is required" }}
                        render={({ field, fieldState }) => (
                          <SliderField
                            id="storage"
                            label={LABELS.Storage}
                            min={storage?.min}
                            max={storage?.max}
                            value={field.value}
                            valueLabel={LABELS.StorageValue}
                            onChange={field.onChange}
                            errorLabel={fieldState.error?.message}
                            required
                            testId="vmTemplate-storage"
                          />
                        )}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* ACTION BUTTONS */}
              <div className="w-full flex justify-end gap-3 bg-[#F5F5F5] border-t border-[#D6D6D6] rounded-b-xl px-6 py-4">
                <SIMPLButtonCustomized
                  label="Cancel"
                  secondary
                  buttonHandlerAction={handleCancel}
                  testId="vmTemplate-button-cancel"
                />
                <SIMPLButtonCustomized
                  label={LABELS.Submit}
                  primary
                  testId="vmTemplate-button-submit"
                />
              </div>
            </form>
          </div>
        </div>
      </LoadingWrapper>
    </SIMPLMenuHeader>
  );
};
